import { observer } from 'owa-mobx-react';
import FocusedInboxHeader from './FocusedInboxHeader';
import MailListHeaderSecondRowTextContent from './MailListHeaderSecondRowTextContent';
import { getEffectiveFolderDisplayName, isPublicFolder } from 'owa-folders';
import { isFocusedInboxEnabled } from 'owa-mail-triage-common';
import isAttachmentsSearchFolder from 'owa-mail-store/lib/utils/isAttachmentsSearchFolder';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import React from 'react';
import { isFavoritingInProgress } from 'owa-mail-favorites-store';
import { lazyToggleFavoriteFolder } from 'owa-mail-folder-forest-actions';
import { lazyRemovePublicFolderFromFavorites } from 'owa-publicfolder-picker';
import { isFolderInFavorites, isPublicFolderInFavorites } from 'owa-favorites';
import { getMailboxInfoFromTableViewId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import type { UserSettingsStyleSelector } from '../../utils/getStyleSelectorAsPerUserSettings';
import { isCapabilitySupported } from 'owa-capabilities';
import { favoritesCapability } from 'owa-capabilities-definitions/lib/favoritesCapability';
import { getStore, type MailFolderTableQuery, isCatchUpFilter } from 'owa-mail-list-store';
import loc from 'owa-localize';
import { catchUpHeaderLabel } from './MailListFolderHeaderSecondRowContent.locstring.json';
import PriorityInboxPivot from './PriorityInboxPivot';
import getCopilotPrioritizeSettings from 'owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings';
import { isCopilotFeatureEnabled } from 'owa-copilot-settings-utils';
import { isFeatureEnabled } from 'owa-feature-flags';
import { setItem } from 'owa-local-storage';

export interface MailListFolderHeaderSecondRowContentProps {
    tableViewId: string;
    containerCssClass: string;
    mailListHeaderStylesAsPerUserSettings?: UserSettingsStyleSelector;
}

export default observer(function MailListFolderHeaderSecondRowContent(
    props: MailListFolderHeaderSecondRowContentProps
) {
    const selectedFolder = getSelectedFolder();
    const isInboxSelected = folderIdToName(selectedFolder?.id ?? '') === 'inbox';
    const mailboxInfo = getMailboxInfoFromTableViewId(props.tableViewId);

    const tableView = getStore().tableViews.get(props.tableViewId);
    const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;
    const isCatchUpFilterSelected = isCatchUpFilter(mailTableQuery);

    const { prioritizationEnabled, experimentalSetting } =
        getCopilotPrioritizeSettings(mailboxInfo);
    const shouldShowPriorityTab =
        isFeatureEnabled('tri-priority-tab-dev2') &&
        isCopilotFeatureEnabled('Inbox', mailboxInfo, true /* skipLanguageCheck */) &&
        prioritizationEnabled &&
        experimentalSetting !== 'false' &&
        isInboxSelected &&
        !isCatchUpFilterSelected;

    if (isInboxSelected && isFocusedInboxEnabled(mailboxInfo) && !isCatchUpFilterSelected) {
        return (
            <FocusedInboxHeader
                tableViewId={props.tableViewId}
                mailListHeaderStylesAsPerUserSettings={props.mailListHeaderStylesAsPerUserSettings}
                shouldShowPriorityTab={shouldShowPriorityTab}
            />
        );
    }

    if (shouldShowPriorityTab && !isFocusedInboxEnabled(mailboxInfo)) {
        return <PriorityInboxPivot tableViewId={props.tableViewId} />;
    }

    // If no pivots are rendered in the Inbox folder - and Priority pivot is not shown -
    // reset the value for "last selected pivot" to "not-priority" so we don't
    // load the "High Priority" filter when the next session begins.
    if (isInboxSelected) {
        setItem(window, `mail_LAST_SELECTED_PIVOT${mailboxInfo.userIdentity}`, 'not-priority');
    }

    if (!selectedFolder) {
        /**
         * This function gets called when when user switches from a favorite persona to a favorite category. At that time, the selected node
         * is set to category node while the table is still persona table. Thus we are expecting the folder to be undefined as the category folder
         * is not saved in folder store.
         */
        return null;
    }
    const selectedFolderId = selectedFolder?.id;

    // We don't currently support adding attachments search folders or PST folders
    const shouldShowFavoriteToggle =
        !isCatchUpFilterSelected &&
        !isAttachmentsSearchFolder(selectedFolderId) &&
        isCapabilitySupported(favoritesCapability, mailboxInfo);

    const isFolderInFavorite =
        !!selectedFolderId &&
        (isPublicFolder(selectedFolderId)
            ? isPublicFolderInFavorites(selectedFolderId)
            : isFolderInFavorites(selectedFolderId));

    return (
        <MailListHeaderSecondRowTextContent
            text={
                isCatchUpFilterSelected
                    ? loc(catchUpHeaderLabel)
                    : getEffectiveFolderDisplayName(selectedFolder)
            }
            containerCssClass={props.containerCssClass}
            showFavoriteToggle={shouldShowFavoriteToggle}
            onFavoriteToggleClick={toggleFavoriteState}
            isInFavorites={isFolderInFavorite}
            styleSelectorAsPerUserSettings={props.mailListHeaderStylesAsPerUserSettings}
            mailboxInfo={mailboxInfo}
            tableView={tableView}
        />
    );
},
'MailListFolderHeaderSecondRowContent');

const toggleFavoriteState = async (evt: React.MouseEvent<unknown>) => {
    evt.stopPropagation();
    const selectedFolder = getSelectedFolder();
    const selectedFolderId = selectedFolder?.id;

    if (!selectedFolder || !selectedFolderId) {
        return;
    }
    if (isFavoritingInProgress(selectedFolderId)) {
        // Do nothing: favoriting is in progress
        return;
    }

    const isFolderInFavorite =
        !!selectedFolderId &&
        (isPublicFolder(selectedFolderId)
            ? isPublicFolderInFavorites(selectedFolderId)
            : isFolderInFavorites(selectedFolderId));

    if (isPublicFolder(selectedFolderId)) {
        const removePublicFolderFromFavorites = await lazyRemovePublicFolderFromFavorites.import();
        removePublicFolderFromFavorites(selectedFolder);
    } else {
        lazyToggleFavoriteFolder.importAndExecute(
            selectedFolderId,
            !!isFolderInFavorite,
            'FolderHeader'
        );
    }
};
